import React, { useRef, useState } from "react";
import { Button } from "react-bootstrap";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import cogoToast from 'cogo-toast';
import { Card, CardBody } from "../../../../../_metronic/_partials/controls";
import { errorMessage, formatDate, formatStatus } from "../../../../util/Helpers";
import ViewAction from "./modals/ViewAction";
import DynamicSimpleTable from "../../../../util/DynamicSimpleReactTable";
import DeleteActionModal from "./modals/DeleteActionModal";
import ApproveActionModal from "./modals/ApproveActionModal";
import DeclineActionModal from "./modals/DeclineActionModal";
import { useSelector } from "react-redux";
import { SubHeader } from "../../../Common/layout/SubHeader";
import { Redirect, Switch } from "react-router-dom";
import { ContentRoute } from "../../../../../_metronic/layout";
import { Permissions } from "../../../../util/Permissions";
import { FilterGroup } from "../../../Common/components/FilterGroup";

const Actions = () => {
    const adminUser = useSelector(state => state.auth.user);
    const tableRef = useRef();

    // Modal Operation State
    const [actionsState, setActionsState] = useState({
        showActionModal: false,
        showDeleteActionModal: false,
        showApproveActionModal: false,
        showDeclineActionModal: false,
        action: null,
        details: null,
    });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setActionsState({ ...actionsState, [modal]: !actionsState[modal] })
    }

    /* Show Specified Modal */
    const showModal = (modal, action) => {
        setActionsState({ ...actionsState, [modal]: true, action: action })
    }

    /* Delete Theme Modal */
    const showDeleteActionModal = details => {
        setActionsState({ ...actionsState, showDeleteActionModal: true, details });
    }

    /* Approve Action Modal */
    const showApproveActionModal = details => {
        setActionsState({ ...actionsState, showApproveActionModal: true, details });
    }

    /* Decline Action Modal */
    const showDeclineActionModal = details => {
        setActionsState({ ...actionsState, showDeclineActionModal: true, details });
    }

    /* Action Buttons */
    const ActionButton = ({ row, showOptions = false }) => {
        const canApproveOrDecline = adminUser?.id !== row?.creatorId && row.approvalStatus === "PENDING"

        return (
            <span style={{ whiteSpace: "nowrap" }}>
                <Button
                    title="View Action"
                    className="btn btn-icon btn-light btn-hover-primary btn-sm"
                    onClick={_ => showModal("showActionModal", row)}

                >
                    <span className="svg-icon svg-icon-md svg-icon-primary">
                        <SVG
                            title="View Action"
                            src={toAbsoluteUrl("/media/svg/icons/General/Eye-2.svg")}
                        />
                    </span>
                </Button>
                {
                    showOptions &&
                    <>
                        <Button
                            title="Approve Action"
                            className="btn btn-icon btn-light btn-hover-primary btn-sm mx-2"
                            onClick={_ => showApproveActionModal(row)}
                            disabled={!canApproveOrDecline}
                        >
                            <span
                                className={`svg-icon svg-icon-md svg-icon-${(!canApproveOrDecline) ? "muted" : "primary"}`}>
                                <SVG
                                    title="Approve Action"
                                    src={toAbsoluteUrl("/media/svg/icons/Navigation/Check.svg")}
                                />
                            </span>
                        </Button>
                        <Button
                            title="Decline Action"
                            className="btn btn-icon btn-light btn-hover-primary btn-sm mx-2"
                            onClick={_ => showDeclineActionModal(row)}
                            disabled={!canApproveOrDecline}
                        >
                            <span
                                className={`svg-icon svg-icon-md svg-icon-${(!canApproveOrDecline) ? "muted" : "primary"}`}>
                                <SVG
                                    title="Decline Action"
                                    src={toAbsoluteUrl("/media/svg/icons/Code/Error-circle.svg")}
                                />
                            </span>
                        </Button>
                        <Button
                            title="Delete Action"
                            className="btn btn-icon btn-light btn-hover-primary btn-sm"
                            onClick={_ => showDeleteActionModal(row)}
                            disabled={row.approvalStatus !== "PENDING"}
                        >
                            <span
                                className={`svg-icon svg-icon-md svg-icon-${row.approvalStatus !== "PENDING" ? "muted" : "danger"}`}>
                                <SVG
                                    title="Delete Action"
                                    src={toAbsoluteUrl("/media/svg/icons/General/Trash.svg")}
                                />
                            </span>
                        </Button>
                    </>
                }
            </span>
        )
    }

    // Tags
    const TagsFilter = () => (
        <FilterGroup.Filter width="300px" name={"tag"} label={"Tag"}>
            <FilterGroup.Option value={""}>All Actions</FilterGroup.Option>
            <FilterGroup.Option value={"account"}>Account Actions</FilterGroup.Option>
            <FilterGroup.Option value={"administrator"}>Administrator Actions</FilterGroup.Option>
            <FilterGroup.Option value={"bank"}>Bank Actions</FilterGroup.Option>
            <FilterGroup.Option value={"bankAccount"}>Bank Account Actions</FilterGroup.Option>
            <FilterGroup.Option value={"card"}>Card Actions</FilterGroup.Option>
            <FilterGroup.Option value={"dividend"}>Dividend Actions</FilterGroup.Option>
            <FilterGroup.Option value={"exchangeRate"}>Exchange Rate Actions</FilterGroup.Option>
            <FilterGroup.Option value={"kyc"}>KYC Actions</FilterGroup.Option>
            <FilterGroup.Option value={"migration"}>Migration Actions</FilterGroup.Option>
            <FilterGroup.Option value={"nuban"}>NUBAN Actions</FilterGroup.Option>
            <FilterGroup.Option value={"order"}>Order Actions</FilterGroup.Option>
            <FilterGroup.Option value={"payment"}>Payment Actions</FilterGroup.Option>
            <FilterGroup.Option value={"savedCard"}>Saved Card Actions</FilterGroup.Option>
            <FilterGroup.Option value={"theme"}>Theme Actions</FilterGroup.Option>
            <FilterGroup.Option value={"transaction"}>Transaction Actions</FilterGroup.Option>
            <FilterGroup.Option value={"userProfile"}>User Profile Actions</FilterGroup.Option>
            <FilterGroup.Option value={"stalledTransaction"}>Flagged Transactions</FilterGroup.Option>
        </FilterGroup.Filter>
    )

    return <>
        <SubHeader>
            <SubHeader.Nav>
                <SubHeader.NavItem label={"Pending"} to={`/overview/actions/pending`} />
                <SubHeader.NavItem label={"All"} to={`/overview/actions/all`} />
                <SubHeader.NavItem label={"Flagged Transactions"} to={`/overview/actions/flaggedTransactions`} />
            </SubHeader.Nav>
        </SubHeader>
        <div className="py-4 px-6">
            <Switch>
                <Redirect exact={true} from="/overview/actions" to="/overview/actions/pending" />

                <ContentRoute path="/overview/actions/pending" permission={Permissions.ACTIONS}>
                    <FilterGroup key={`pending-actions`}>
                        <div className={"mb-3 d-flex"}>
                            <TagsFilter />
                        </div>
                        <FilterGroup.Content>
                            {
                                ({ filters }) => (
                                    <Card>
                                        <CardBody>
                                            <DynamicSimpleTable
                                                source={{ url: `/admins/actions` }}
                                                queryParams={
                                                    `approvalStatus=pending`
                                                    + `&tag=${filters?.tag && filters.tag !== "" ? filters.tag : 'exclude-stalled'}`
                                                }
                                                columns={[
                                                    { title: "ACTION", key: "action" },
                                                    { title: "BY", key: "actionCreator" },
                                                    { title: "ON", key: "actedOn" },
                                                    { title: "APPROVAL", format: row => formatStatus(row.approvalStatus) },
                                                    { title: "DATE", format: row => formatDate(row.createdAt) },
                                                    { title: "", format: (row) => <ActionButton row={row} showOptions /> }
                                                ]}
                                                ref={tableRef}
                                                onError={err => cogoToast.error(errorMessage(err))}
                                                refreshIcon={true}
                                            />
                                        </CardBody>
                                    </Card>
                                )
                            }
                        </FilterGroup.Content>
                    </FilterGroup>
                </ContentRoute>
                <ContentRoute path="/overview/actions/all" permission={Permissions.ACTIONS}>
                    <FilterGroup key={`pending-actions`}>
                        <div className={"mb-3 d-flex"}>
                            <TagsFilter />
                        </div>
                        <FilterGroup.Content>
                            {
                                ({ filters }) => (
                                    <Card>
                                        <CardBody>
                                            <DynamicSimpleTable
                                                source={{ url: `/admins/actions` }}
                                                queryParams={
                                                    `&tag=${filters?.tag || ""}`
                                                }
                                                columns={[
                                                    { title: "ACTION", key: "action" },
                                                    { title: "CREATED BY", key: "actionCreator" },
                                                    { title: "ON", key: "actedOn" },
                                                    { title: "APPROVAL", format: row => formatStatus(row.approvalStatus) },
                                                    { title: "VALIDATED BY", format: row => row.actionApprover || "" },
                                                    { title: "DATE", format: row => formatDate(row.createdAt) },
                                                    { title: "STATUS", format: row => formatStatus(row.status) },
                                                    { title: "", format: (row) => <ActionButton row={row} /> }
                                                ]}

                                                onError={err => cogoToast.error(errorMessage(err))}
                                                refreshIcon={true}
                                            />
                                        </CardBody>
                                    </Card>
                                )
                            }
                        </FilterGroup.Content>
                    </FilterGroup>
                </ContentRoute>
                <ContentRoute path="/overview/actions/flaggedTransactions" permission={Permissions.ACTIONS}>
                    <FilterGroup key={`pending-actions`}>
                        <div className={"mb-3 d-flex"}>
                            <TagsFilter />
                        </div>
                        <FilterGroup.Content>
                            {
                                ({ filters }) => (
                                    <Card>
                                        <CardBody>
                                            <DynamicSimpleTable
                                                source={{ url: `/admins/actions` }}
                                                queryParams={
                                                    `approvalStatus=pending`
                                                    + `&tag=${filters?.tag || "stalledTransaction"}`
                                                }
                                                columns={[
                                                    { title: "ACTION", key: "action" },
                                                    { title: "CREATED BY", key: "actionCreator" },
                                                    { title: "ON", key: "actedOn" },
                                                    { title: "APPROVAL", format: row => formatStatus(row.approvalStatus) },
                                                    { title: "VALIDATED BY", format: row => row.actionApprover || "" },
                                                    { title: "DATE", format: row => formatDate(row.createdAt) },
                                                    { title: "STATUS", format: row => formatStatus(row.status) },
                                                    { title: "", format: (row) => <ActionButton row={row} showOptions /> }
                                                ]}
                                                ref={tableRef}
                                                onError={err => cogoToast.error(errorMessage(err))}
                                                refreshIcon={true}
                                            />
                                        </CardBody>
                                    </Card>
                                )
                            }
                        </FilterGroup.Content>
                    </FilterGroup>
                </ContentRoute>
            </Switch>
        </div>

        <ViewAction
            show={actionsState.showActionModal}
            onHide={toggleModal("showActionModal")}
            action={actionsState.action}
        />

        <ApproveActionModal
            {...actionsState}
            onHide={toggleModal("showApproveActionModal")}
            tableRef={tableRef}
        />

        <DeclineActionModal
            {...actionsState}
            onHide={toggleModal("showDeclineActionModal")}
            tableRef={tableRef}
        />

        <DeleteActionModal
            {...actionsState}
            onHide={toggleModal("showDeleteActionModal")}
            tableRef={tableRef}
        />
    </>
};

export default Actions;
